export interface NamedOption {
  id: number;
  name: string;
}

export interface CurrencyOption {
  id: number;
  code: string;
  name: string;
}

export interface PersonOption {
  id: number;
  email: string;
  name: string | null;
}

export interface AdminOrgPermissions {
  selection: boolean;
  create: boolean;
  create_bulk: boolean;
  edit: boolean;
  edit_bulk: boolean;
  user_management: boolean;
  view_all_organizations: boolean;
}

export interface OrgsMeta {
  tiers: NamedOption[];
  currencies: CurrencyOption[];
  account_managers: PersonOption[];
  current_org_id: number | null;
  current_user_id: number;
  permissions: AdminOrgPermissions;
}

export interface AdminOrg {
  id: number;
  name: string;
  is_active: boolean;
  tier_id: number | null;
  tier_name: string | null;
  currency_id: number | null;
  currency_code: string | null;
  account_manager_id: number | null;
  account_manager_name: string | null;
  account_manager_email: string | null;
  org_admin_id: number | null;
  org_admin_name: string | null;
  org_admin_email: string | null;
  client_member_count: number;
  created_at: string | null;
}

export interface OrgFormValue {
  name: string;
  tier_id: number | '';
  currency_id: number | '';
  account_manager_id: number | '';
  org_admin_id: number | '';
  org_admin_email: string;
  is_active: boolean;
}

export interface OrgListResponse {
  count: number;
  page: number;
  page_size: number;
  results: AdminOrg[];
}

export interface BulkRowResult {
  row?: number;
  id?: number;
  name: string;
  status: string;
  message: string;
}

export interface BulkCreateResponse {
  dry_run: boolean;
  created: number;
  errors: number;
  results: BulkRowResult[];
}

export interface BulkActionResponse {
  updated: number;
  errors: number;
  results: BulkRowResult[];
}

export const ORGS_SUBTAB_CAPS = {
  selection: 'admin_panel_organizations_selection',
  create: 'admin_panel_organizations_create',
  createBulk: 'admin_panel_organizations_create_bulk',
  edit: 'admin_panel_organizations_edit',
  editBulk: 'admin_panel_organizations_edit_bulk',
  userManagement: 'admin_panel_organization_user_management',
} as const;

export const emptyOrgForm = (meta?: OrgsMeta | null): OrgFormValue => {
  const actorIsManager = meta?.account_managers.some((m) => m.id === meta.current_user_id);
  const chf = meta?.currencies.find((c) => c.code === 'CHF');
  return {
    name: '',
    tier_id: '',
    currency_id: chf?.id ?? '',
    account_manager_id: meta && actorIsManager ? meta.current_user_id : '',
    org_admin_id: '',
    org_admin_email: '',
    is_active: true,
  };
};

export function formFromOrg(org: AdminOrg): OrgFormValue {
  return {
    name: org.name || '',
    tier_id: org.tier_id ?? '',
    currency_id: org.currency_id ?? '',
    account_manager_id: org.account_manager_id ?? '',
    org_admin_id: org.org_admin_id ?? '',
    org_admin_email: '',
    is_active: org.is_active,
  };
}

export function formToPayload(value: OrgFormValue, mode: 'create' | 'edit') {
  const payload: Record<string, unknown> = {
    name: value.name.trim(),
    tier_id: value.tier_id === '' ? null : value.tier_id,
    currency_id: value.currency_id === '' ? null : value.currency_id,
    account_manager_id: value.account_manager_id === '' ? null : value.account_manager_id,
  };
  if (value.org_admin_id !== '') {
    payload.org_admin_id = value.org_admin_id;
  } else if (value.org_admin_email.trim()) {
    payload.org_admin_email = value.org_admin_email.trim();
  } else if (mode === 'edit') {
    payload.org_admin_id = null;
  }
  if (mode === 'edit') {
    payload.is_active = value.is_active;
  }
  return payload;
}
